import { ChevronDown } from 'lucide-react';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

const FILTER_ALL_VALUE = '__all__';

const COMPACT_FIELD_CLASS = 'w-[10.5rem] shrink-0';
const COMPACT_SELECT_TRIGGER_CLASS =
	'h-9 rounded-xl border-stone-200 bg-white px-3 text-xs font-semibold text-zinc-600 shadow-sm';
const COMPACT_LABEL_CLASS =
	'mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-slate-500';

const MONTH_OPTIONS = [
	{ value: '1', label: 'Janeiro' },
	{ value: '2', label: 'Fevereiro' },
	{ value: '3', label: 'Março' },
	{ value: '4', label: 'Abril' },
	{ value: '5', label: 'Maio' },
	{ value: '6', label: 'Junho' },
	{ value: '7', label: 'Julho' },
	{ value: '8', label: 'Agosto' },
	{ value: '9', label: 'Setembro' },
	{ value: '10', label: 'Outubro' },
	{ value: '11', label: 'Novembro' },
	{ value: '12', label: 'Dezembro' },
];

const buildYearOptions = () => {
	const currentYear = new Date().getUTCFullYear();
	const years: Array<{ value: string; label: string }> = [];
	for (let year = currentYear; year >= currentYear - 10; year -= 1) {
		years.push({ value: String(year), label: String(year) });
	}
	return years;
};

interface ProfessionalMonthlyUsageFiltersProps {
	usageYear?: number;
	usageMonth?: number;
	onUsageYearChange: (year?: number) => void;
	onUsageMonthChange: (month?: number) => void;
}

const ProfessionalMonthlyUsageFilters = ({
	usageYear,
	usageMonth,
	onUsageYearChange,
	onUsageMonthChange,
}: ProfessionalMonthlyUsageFiltersProps) => {
	const yearOptions = buildYearOptions();
	const yearValue = usageYear ? String(usageYear) : FILTER_ALL_VALUE;
	const monthValue = usageMonth ? String(usageMonth) : FILTER_ALL_VALUE;

	return (
		<div className="flex flex-wrap items-end gap-3">
			<div className={COMPACT_FIELD_CLASS}>
				<label className={COMPACT_LABEL_CLASS}>Ano</label>
				<Select
					value={yearValue}
					onValueChange={(next) =>
						onUsageYearChange(
							next === FILTER_ALL_VALUE
								? undefined
								: Number(next),
						)
					}
				>
					<SelectTrigger
						className={COMPACT_SELECT_TRIGGER_CLASS}
						hideTriggerIcon
					>
						<span
							className={cn(
								'truncate',
								!usageYear && 'font-normal text-zinc-400',
							)}
						>
							{usageYear ?? 'Todos'}
						</span>
						<ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 opacity-50" />
					</SelectTrigger>
					<SelectContent className="bg-white">
						<SelectItem value={FILTER_ALL_VALUE} indicator={false}>
							Todos
						</SelectItem>
						{yearOptions.map((option) => (
							<SelectItem
								key={option.value}
								value={option.value}
								indicator={false}
							>
								{option.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			<div className={COMPACT_FIELD_CLASS}>
				<label className={COMPACT_LABEL_CLASS}>Mês</label>
				<Select
					value={monthValue}
					onValueChange={(next) =>
						onUsageMonthChange(
							next === FILTER_ALL_VALUE
								? undefined
								: Number(next),
						)
					}
				>
					<SelectTrigger
						className={COMPACT_SELECT_TRIGGER_CLASS}
						hideTriggerIcon
					>
						<span
							className={cn(
								'truncate',
								!usageMonth && 'font-normal text-zinc-400',
							)}
						>
							{usageMonth
								? MONTH_OPTIONS.find(
										(option) =>
											Number(option.value) === usageMonth,
								  )?.label
								: 'Todos'}
						</span>
						<ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 opacity-50" />
					</SelectTrigger>
					<SelectContent className="bg-white">
						<SelectItem value={FILTER_ALL_VALUE} indicator={false}>
							Todos
						</SelectItem>
						{MONTH_OPTIONS.map((option) => (
							<SelectItem
								key={option.value}
								value={option.value}
								indicator={false}
							>
								{option.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
		</div>
	);
};

export { ProfessionalMonthlyUsageFilters };
